import type {
  EmailEventSettingPublic,
  EmailSettingsResponse,
  UpdateEmailSettingsRequest,
} from '../../../lib/api';

/**
 * The Email & notifications form's draft (SIR-7804). Pure, so the component file exports
 * components only. The API key is WRITE-ONLY: the draft starts it empty on every mount and
 * nothing the server sends can fill it.
 */
export interface EmailDraft {
  masterEnabled: boolean;
  fromAddress: string;
  senderName: string;
  /** Empty means "use From"; sent as null. */
  replyTo: string;
  /** A new key to store. Empty keeps the stored one. */
  apiKey: string;
  /** Remove the stored key on save, back to the environment fallback. */
  clearKey: boolean;
  events: EmailEventSettingPublic[];
}

export function emailDraftFrom(saved: EmailSettingsResponse): EmailDraft {
  const { settings } = saved;
  return {
    masterEnabled: settings.masterEnabled,
    fromAddress: settings.fromAddress,
    senderName: settings.senderName,
    replyTo: settings.replyTo ?? '',
    apiKey: '',
    clearKey: false,
    events: settings.events.map((event) => ({ ...event })),
  };
}

/** What a save would change, so a landed save remounts the form with a clean draft. */
export function emailSavedSignature(saved: EmailSettingsResponse): string {
  const { settings, status } = saved;
  return JSON.stringify([
    settings.masterEnabled,
    settings.fromAddress,
    settings.senderName,
    settings.replyTo,
    settings.events.map((event) => [event.key, event.emailEnabled, event.inAppEnabled]),
    status.sendGridApiKeyConfigured,
    status.sendGridApiKeyLast4,
  ]);
}

export function emailDraftDirty(draft: EmailDraft, saved: EmailSettingsResponse): boolean {
  const { settings } = saved;
  if (draft.apiKey.trim() !== '' || draft.clearKey) return true;
  if (draft.masterEnabled !== settings.masterEnabled) return true;
  if (draft.fromAddress !== settings.fromAddress) return true;
  if (draft.senderName !== settings.senderName) return true;
  if (draft.replyTo !== (settings.replyTo ?? '')) return true;
  return draft.events.some((event) => {
    const before = settings.events.find((row) => row.key === event.key);
    return (
      before === undefined ||
      before.emailEnabled !== event.emailEnabled ||
      before.inAppEnabled !== event.inAppEnabled
    );
  });
}

export type EmailDraftProblem = 'fromRequired' | 'senderRequired';

/**
 * The PUT body, or the first problem the form can see. The server validates everything
 * again (addresses included); this is affordance only.
 */
export function emailRequestFrom(
  draft: EmailDraft,
): { ok: true; body: UpdateEmailSettingsRequest } | { ok: false; problem: EmailDraftProblem } {
  const fromAddress = draft.fromAddress.trim();
  if (fromAddress === '') return { ok: false, problem: 'fromRequired' };
  const senderName = draft.senderName.trim();
  if (senderName === '') return { ok: false, problem: 'senderRequired' };
  const replyTo = draft.replyTo.trim();
  const apiKey = draft.apiKey.trim();

  const body: UpdateEmailSettingsRequest = {
    masterEnabled: draft.masterEnabled,
    fromAddress,
    senderName,
    replyTo: replyTo === '' ? null : replyTo,
    events: draft.events.map((event) => ({
      key: event.key,
      emailEnabled: event.emailEnabled,
      inAppEnabled: event.inAppEnabled,
    })),
  };
  // Omitted keeps the stored key: an empty field never reaches the server as a blank key.
  if (apiKey !== '') body.sendGridApiKey = apiKey;
  else if (draft.clearKey) body.sendGridApiKey = null;
  return { ok: true, body };
}
